import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FilterOption } from '../../models/ticket.models';

/**
 * A row of filter pills.
 *
 * Deliberately NOT `btn btn-sm`. Bootstrap's button sizing is built for actions
 * in a page-width toolbar, and this row lives in a ~330px sidebar: five priority
 * options wrapped onto two lines, four status options onto two more, and the
 * filters alone took more vertical space than the first case in the list they
 * filter. `btn-primary` also painted the selected option a saturated blue that
 * outweighed the severity colours on the cards below — the loudest thing on the
 * panel was the word "All".
 *
 * These are pills sized for a narrow column, with selection carried by a quiet
 * inversion rather than a fill that competes with the data.
 */
@Component({
  selector: 'app-case-filter-chips',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="chip-row" role="group">
      @for (filter of filters(); track filter.label) {
        <button
          type="button"
          class="chip"
          [class.is-active]="activeValue() === filter.value"
          [attr.aria-pressed]="activeValue() === filter.value"
          (click)="filterChanged.emit(filter.value)"
        >
          {{ filter.label }}
        </button>
      }
    </div>
  `,
  styles: [
    `
      .chip-row {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
      }

      .chip {
        padding: 3px 10px;
        border: 1px solid #d0d5dd;
        border-radius: 999px;
        background: #fff;
        color: #475467;
        font-size: 0.688rem;
        font-weight: 500;
        line-height: 1.45;
        white-space: nowrap;
        transition:
          background-color 0.12s ease,
          border-color 0.12s ease,
          color 0.12s ease;
      }

      .chip:hover:not(.is-active) {
        border-color: #98a2b3;
        color: #101828;
      }

      /* Selection reads as a state, not as a call to action. */
      .chip.is-active {
        background: #101828;
        border-color: #101828;
        color: #fff;
      }

      .chip:focus-visible {
        outline: 2px solid #49419a;
        outline-offset: 1px;
      }
    `,
  ],
})
export class CaseFilterChipsComponent {
  filters = input.required<FilterOption[]>();
  activeValue = input<string | null>(null);
  filterChanged = output<string | null>();
}
